class NpsCards extends HTMLElement {
  constructor() {
    super();
    this.tl = null;
    this.entranceTriggers = [];
    this._observer = null;
    this._pinTrigger = null;
    this._progressDriven = false;
    this._currentTL = null;
    this._isCarousel = false;
    this._isScrub = false;
    this._cardObserver = null;
    this._onResize = this._debounce(() => this._init(), 300);
  }

  connectedCallback() {
    if (this.connectedCallbackCalled) return;
    this.connectedCallbackCalled = true;

    this.cards = Array.from(this.querySelectorAll('.nps__card'));
    this.section = this.closest('.nps');

    if (!this.cards.length) return;

    this._ensureGsap(() => this._init());

    window.addEventListener('resize', this._onResize);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) {
          this._destroy();
          setTimeout(() => this._init(), 500);
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        const card = e.target.closest('.nps__card');
        if (card && this.contains(card)) {
          const index = this.cards.indexOf(card);
          if (index > -1) {
            if (this._isCarousel) {
              this.scrollTo({ left: card.offsetLeft - 24, behavior: 'smooth' });
            } else if (this._isScrub && this._pinTrigger) {
              const maxScroll = this.scrollWidth - this.clientWidth;
              const cardProgress = Math.min(1, card.offsetLeft / Math.max(maxScroll, 1));
              const scrollTarget = this._pinTrigger.start + cardProgress * (this._pinTrigger.end - this._pinTrigger.start);
              window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
            } else {
              this._goToCard(index);
            }
          }
        }
      });
    }
  }

  disconnectedCallback() {
    requestAnimationFrame(() => {
      if (!this.isConnected) {
        window.removeEventListener('resize', this._onResize);
        this._destroy();
      }
    });
  }

  _ensureGsap(cb) {
    const ready = () =>
      typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

    if (ready()) {
      gsap.registerPlugin(ScrollTrigger);
      if (typeof Observer !== 'undefined') gsap.registerPlugin(Observer);
      cb();
      return;
    }

    const loadScript = (src) =>
      new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        document.head.appendChild(s);
      });

    loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js')
      .then(() => loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js'))
      .then(() => loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/Observer.min.js'))
      .then(() => {
        if (ready()) {
          gsap.registerPlugin(ScrollTrigger);
          if (typeof Observer !== 'undefined') gsap.registerPlugin(Observer);
          cb();
        }
      });
  }

  _init() {
    this._destroy();
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    gsap.registerPlugin(ScrollTrigger);
    if (typeof Observer !== 'undefined') gsap.registerPlugin(Observer);

    if (window.innerWidth < 750) {
      const layout = this.dataset.mobileLayout || 'stacking';
      if (layout === 'carousel') {
        this._initMobileCarousel();
      } else if (layout === 'scrub') {
        this._initMobileScrub();
      } else {
        this._initMobileStack();
      }
    } else {
      this._initDesktopEntrance();
    }
  }

  /* ───────────────────────────────────────────
     Shared: animate a single card's ring + text
     ─────────────────────────────────────────── */
  _animateCardRing(card, delay) {
    // Kill any in-flight ring tween so the count can resume from current value
    if (card._ringTween) card._ringTween.kill();

    const target = parseFloat(card.dataset.statTarget) || 0;
    const value = card.querySelector('.nps__card-value');
    const current = parseFloat(card.style.getPropertyValue('--stat-value')) || 0;
    const remaining = Math.max(0, target - current);
    const fullRange = Math.max(target, 1);
    // Shorten duration proportionally so resumed counts don't feel sluggish
    const duration = Math.max(0.2, 0.8 * (remaining / fullRange));
    const proxy = { v: current };

    card._ringTween = gsap.to(proxy, {
      v: target,
      duration: duration,
      delay: delay || 0,
      ease: 'power2.out',
      onUpdate: () => {
        card.style.setProperty('--stat-value', proxy.v);
        if (value) {
          // Show decimal only when the target has one and we're in the last integer
          var hasDecimal = target % 1 !== 0;
          if (hasDecimal && proxy.v >= Math.floor(target)) {
            value.textContent = proxy.v.toFixed(1) + '%';
          } else {
            value.textContent = Math.round(proxy.v) + '%';
          }
        }
      },
      onComplete: () => {
        card.style.setProperty('--stat-value', target);
        if (value) value.textContent = card.dataset.statTarget + '%';
        card._ringTween = null;
        // Only now can we say this card is fully revealed — interrupted
        // reveals leave the flag false so a later visit re-animates.
        card._revealed = true;
      },
    });
  }

  _animateSummary() {
    const col = this.closest('.nps__right');
    if (!col) return;
    col.querySelectorAll('.nps__summary-card').forEach((card) => {
      const target = parseFloat(card.dataset.summaryValue) || 0;
      const numEl = card.querySelector('.nps__summary-num');
      if (!numEl) return;
      const proxy = { value: 0 };
      gsap.to(proxy, {
        value: target,
        duration: 1,
        ease: 'power2.out',
        onUpdate: () => { numEl.textContent = Math.round(proxy.value); },
      });
    });
  }

  /* ───────────────────────────────────────────
     Desktop: staggered fade-up + ring fill
     ─────────────────────────────────────────── */
  _initDesktopEntrance() {
    this.classList.remove('is-stacking');
    this.style.height = '';
    this.cards.forEach((card) => {
      card.style.transform = '';
      card.style.position = '';
      card.style.zIndex = '';
      card.style.height = '';
      card.style.setProperty('--stat-value', '0');
      const v = card.querySelector('.nps__card-value');
      if (v) v.textContent = '0%';
    });

    // Mouse-tracking highlight
    this._onCardMouseMove = (e) => {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--mouse-x', (e.clientX - rect.left) + 'px');
      card.style.setProperty('--mouse-y', (e.clientY - rect.top) + 'px');
    };
    this.cards.forEach((card) => {
      card.addEventListener('mousemove', this._onCardMouseMove);
    });

    const col = this.closest('.nps__right');
    const summaryCards = col ? Array.from(col.querySelectorAll('.nps__summary-card')) : [];
    const allAnimItems = [...this.cards, ...summaryCards];
    gsap.set(allAnimItems, { opacity: 0, y: 60 });

    const entranceTl = gsap.timeline({
      scrollTrigger: { trigger: this, start: 'top 85%', once: true },
    });

    entranceTl.to(allAnimItems, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      stagger: 0.15,
      ease: 'power2.out',
      onStart: () => {
        this.cards.forEach((card, i) => this._animateCardRing(card, i * 0.15));
        this._animateSummary();
      },
    });

    this.entranceTriggers.push(entranceTl.scrollTrigger);

    const header = this.section.querySelector('.nps__header');
    if (header) {
      gsap.set(header, { opacity: 0, y: 40 });
      const headerTrigger = ScrollTrigger.create({
        trigger: this.section,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(header, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' });
        },
      });
      this.entranceTriggers.push(headerTrigger);
    }
  }

  /* ───────────────────────────────────────────
     Mobile: horizontal scroll-scrub
     ─────────────────────────────────────────── */
  _initMobileScrub() {
    const totalCards = this.cards.length;
    this._isScrub = true;

    this.classList.add('is-scrub');
    const col = this.closest('.nps__cards-col');
    if (col) col.classList.add('nps__cards-col--scrub');

    // Clear any leftover stacking inline styles
    this.classList.remove('is-stacking');
    this.style.height = '';
    this.cards.forEach((card) => {
      card.style.transform = '';
      card.style.position = '';
      card.style.zIndex = '';
      card.style.height = '';
    });

    // Hide card content, set summary instantly
    this.cards.forEach((card) => this._resetCardContent(card));
    this._setSummaryFinal();

    // Navigation dots — horizontal, placed between cards and summary
    this._dots = document.createElement('div');
    this._dots.className = 'nps__nav-dots nps__nav-dots--carousel';
    const maxScroll = this.scrollWidth - this.clientWidth;
    for (let i = 0; i < totalCards; i++) {
      const dot = document.createElement('button');
      dot.className = 'nps__nav-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Card ' + (i + 1));
      dot.addEventListener('click', () => {
        if (!this._pinTrigger) return;
        const ms = this.scrollWidth - this.clientWidth;
        const cardProgress = Math.min(1, this.cards[i].offsetLeft / Math.max(ms, 1));
        const scrollTarget = this._pinTrigger.start + cardProgress * (this._pinTrigger.end - this._pinTrigger.start);
        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      });
      this._dots.appendChild(dot);
    }
    if (col && col.parentNode) {
      col.parentNode.insertBefore(this._dots, col.nextSibling);
    }

    // Pin section + scrub horizontal scroll
    this._pinTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top top',
      end: () => '+=' + (this.scrollWidth - this.clientWidth),
      pin: true,
      pinSpacing: true,
      scrub: 0.5,
      onUpdate: (self) => {
        const ms = this.scrollWidth - this.clientWidth;
        this.scrollLeft = self.progress * ms;

        // Find most-visible card → update dots + reveal content
        const viewLeft = this.scrollLeft;
        const viewRight = viewLeft + this.clientWidth;
        let bestIndex = 0;
        let bestOverlap = 0;

        this.cards.forEach((card, i) => {
          const cL = card.offsetLeft;
          const cR = cL + card.offsetWidth;
          const overlap = Math.max(0, Math.min(cR, viewRight) - Math.max(cL, viewLeft));
          if (overlap > bestOverlap) { bestOverlap = overlap; bestIndex = i; }
          if (overlap / card.offsetWidth > 0.5 && !card._revealed) {
            this._revealCardContent(i);
          }
        });

        if (this._dots) {
          this._dots.querySelectorAll('.nps__nav-dot').forEach((d, i) => {
            d.classList.toggle('is-active', i === bestIndex);
          });
        }
      },
    });
  }

  /* ───────────────────────────────────────────
     Mobile: horizontal scroll-snap carousel
     ─────────────────────────────────────────── */
  _initMobileCarousel() {
    const totalCards = this.cards.length;
    this._isCarousel = true;

    this.classList.add('is-carousel');
    const col = this.closest('.nps__cards-col');
    if (col) col.classList.add('nps__cards-col--carousel');

    // Clear any leftover stacking inline styles
    this.classList.remove('is-stacking');
    this.style.height = '';
    this.cards.forEach((card) => {
      card.style.transform = '';
      card.style.position = '';
      card.style.zIndex = '';
      card.style.height = '';
    });

    // Hide card content, set summary instantly
    this.cards.forEach((card) => this._resetCardContent(card));
    this._setSummaryFinal();

    // Navigation dots with timer rings
    this._dots = document.createElement('div');
    this._dots.className = 'nps__nav-dots nps__nav-dots--carousel';
    for (let i = 0; i < totalCards; i++) {
      const dot = document.createElement('button');
      dot.className = 'nps__nav-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Card ' + (i + 1));
      dot.addEventListener('click', () => {
        this.scrollTo({ left: this.cards[i].offsetLeft - 24, behavior: 'smooth' });
      });
      this._dots.appendChild(dot);
    }
    if (col && col.parentNode) {
      col.parentNode.insertBefore(this._dots, col.nextSibling);
    }

    // IntersectionObserver: track active card + reveal content on first visit
    this._cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        const card = entry.target;
        const index = this.cards.indexOf(card);
        if (index === -1) return;

        if (this._dots) {
          this._dots.querySelectorAll('.nps__nav-dot').forEach((d, i) => {
            d.classList.toggle('is-active', i === index);
          });
        }

        if (!card._revealed) {
          this._revealCardContent(index);
        }

        // Restart timer ring on the new active dot
        if (!this._autoplayPaused) {
          this._stopAutoplay();
          this._startAutoplay();
        }
      });
    }, {
      root: this,
      threshold: 0.5,
    });

    this.cards.forEach((card) => this._cardObserver.observe(card));

    // Reveal first card when section scrolls into view
    const entranceTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top 80%',
      once: true,
      onEnter: () => this._revealCardContent(0),
    });
    this.entranceTriggers.push(entranceTrigger);

    // Auto-play: start/stop based on section visibility
    this._autoplayInterval = (parseInt(this.dataset.autoplaySpeed) || 4) * 1000;
    this._autoplayTimer = null;
    this._autoplayPaused = false;

    this._sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._startAutoplay();
        } else {
          this._stopAutoplay();
        }
      });
    }, { threshold: 0.3 });
    this._sectionObserver.observe(this.section);

    // Pause on touch, resume after
    this._onTouchStart = () => this._pauseAutoplay();
    this._onTouchEnd = () => this._resumeAutoplay();
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.addEventListener('touchend', this._onTouchEnd, { passive: true });
  }

  /* ───────────────────────────────────────────
     Mobile: scroll-progress card paginator
     ─────────────────────────────────────────── */
  _initMobileStack() {
    if (this.cards.length < 2) {
      this._initDesktopEntrance();
      return;
    }

    const cardHeight = this.cards[0].offsetHeight;
    const totalCards = this.cards.length;

    if (!cardHeight) {
      setTimeout(() => this._initMobileStack(), 200);
      return;
    }

    this.classList.add('is-stacking');
    const stackCol = this.closest('.nps__cards-col');
    if (stackCol) stackCol.classList.add('nps__cards-col--stacking');

    this._currentCard = 0;
    this._animating = false;
    this._cardHeight = cardHeight;
    this._totalCards = totalCards;
    this._scaleStep = 0.04;
    this._centerOffset = 32;

    // Decreasing peek gaps: first card behind = 32px, then smaller
    this._peekGaps = [32, 28, 22, 16, 12, 10, 8, 6];

    // Cumulative Y offset for a card at stack position `offset` (0 = active)
    this._stackY = (offset) => {
      let y = 0;
      for (let i = 0; i < offset; i++) {
        y += this._peekGaps[Math.min(i, this._peekGaps.length - 1)];
      }
      return y;
    };

    const totalPeek = this._stackY(totalCards - 1);
    const containerHeight = cardHeight + totalPeek;
    this.style.height = containerHeight + 'px';

    // Position all cards stacked, inner content hidden
    this.cards.forEach((card, i) => {
      card.style.height = cardHeight + 'px';
      gsap.set(card, {
        y: this._centerOffset + this._stackY(i),
        zIndex: totalCards - i,
        opacity: 1,
        scale: 1 - i * this._scaleStep,
        transformOrigin: 'top center',
      });
      this._resetCardContent(card);
    });

    // Navigation bullets
    this._dots = document.createElement('div');
    this._dots.className = 'nps__nav-dots';
    for (let i = 0; i < totalCards; i++) {
      const dot = document.createElement('button');
      dot.className = 'nps__nav-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Card ' + (i + 1));
      dot.addEventListener('click', () => {
        if (!this._animating && this._currentCard !== i) {
          this._goToCard(i);
        }
      });
      this._dots.appendChild(dot);
    }
    this.appendChild(this._dots);

    // Reveal first card's content + summary on scroll-into-view
    const ringTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        this._revealCardContent(0);
        this._animateSummary();
      },
    });
    this.entranceTriggers.push(ringTrigger);

    // Progress-based card switching
    this._progressDriven = true;

    const deadZone = 0.10;
    const activeRange = 1 - 2 * deadZone;

    this._cardForProgress = (progress) => {
      if (totalCards <= 1) return 0;
      if (progress <= deadZone) return 0;
      if (progress >= 1 - deadZone) return totalCards - 1;
      const normalized = (progress - deadZone) / activeRange;
      return Math.min(Math.max(Math.round(normalized * (totalCards - 1)), 0), totalCards - 1);
    };

    // Pin the section
    this._pinTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top top',
      end: () => '+=' + Math.round((totalCards - 1) * cardHeight * 0.7),
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      onUpdate: (self) => {
        const targetCard = this._cardForProgress(self.progress);
        if (targetCard === this._currentCard) return;
        this._goToCard(targetCard);
      },
    });
  }

  _releaseScroll(direction) {
    if (!this._observer || !this._pinTrigger) return;
    this._observer.disable();
    const st = this._pinTrigger;
    const target = direction === 'down' ? st.end + 1 : st.start - 1;
    window.scrollTo({ top: target, behavior: 'smooth' });
  }

  _goToCard(index) {
    if (index < 0 || index >= this._totalCards) return;
    if (index === this._currentCard) return;

    // Kill any in-flight tweens so a new target takes over from the current
    // state — interrupted animations "catch up" instead of queuing.
    if (this._currentTL) {
      this._currentTL.kill();
      this._currentTL = null;
    }
    gsap.killTweensOf(this.cards);
    this.cards.forEach((c) => {
      gsap.killTweensOf(c.querySelectorAll('.nps__card-chart, .nps__card-value, .nps__card-desc'));
      if (c._ringTween) { c._ringTween.kill(); c._ringTween = null; }
    });

    this._animating = true;
    const prev = this._currentCard;
    const goingForward = index > prev;
    this._currentCard = index;

    // Update dots
    if (this._dots) {
      this._dots.querySelectorAll('.nps__nav-dot').forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
      });
    }

    // Release scroll immediately when landing on first or last card
    const atBoundary = index === 0 || index === this._totalCards - 1;

    // Reveal destination content on forward OR when back-scrolling into a
    // card we skipped past (so its ring/text isn't empty on arrival).
    const targetCardEl = this.cards[index];
    const needsReveal = goingForward || !targetCardEl._revealed;

    const tl = this._currentTL = gsap.timeline({
      onComplete: () => {
        this._animating = false;
        this._currentTL = null;

        if (!this._progressDriven) {
          if (atBoundary && index === this._totalCards - 1) {
            this._releaseScroll('down');
          } else if (atBoundary && index === 0) {
            this._releaseScroll('up');
          }
        }
      },
    });

    if (needsReveal) {
      this._resetCardContent(targetCardEl);
    }

    this.cards.forEach((card, i) => {
      const offset = i - index;

      if (offset < 0) {
        tl.to(card, {
          y: this._centerOffset - (this._cardHeight - this._peekGaps[0]),
          opacity: 0,
          scale: 1,
          duration: 0.25,
          ease: 'power2.in',
        }, 0);
      } else {
        tl.to(card, {
          y: this._centerOffset + this._stackY(offset),
          opacity: 1,
          scale: 1 - offset * this._scaleStep,
          duration: 0.4,
          ease: 'power2.inOut',
        }, 0);

        // Going backwards: smoothly fade out content on cards after active
        if (!goingForward && offset > 0) {
          this._fadeOutCardContent(card);
        }
      }
    });

    if (needsReveal) {
      this._revealCardContent(index, 0.1);
    }
  }

  _revealCardContent(index, delay) {
    const card = this.cards[index];
    if (!card) return;

    const d = delay || 0;
    const chart = card.querySelector('.nps__card-chart');
    const value = card.querySelector('.nps__card-value');
    const desc = card.querySelector('.nps__card-desc');

    this._animateCardRing(card, d);

    if (chart) gsap.to(chart, { opacity: 1, scale: 1, duration: 0.35, delay: d, ease: 'power2.out' });
    if (value) gsap.to(value, { opacity: 1, y: 0, duration: 0.3, delay: d, ease: 'power2.out' });
    if (desc) gsap.to(desc, { opacity: 1, y: 0, duration: 0.3, delay: d + 0.1, ease: 'power2.out' });
  }

  _fadeOutCardContent(card) {
    card._revealed = false;
    if (card._ringTween) { card._ringTween.kill(); card._ringTween = null; }

    const chart = card.querySelector('.nps__card-chart');
    const value = card.querySelector('.nps__card-value');
    const desc = card.querySelector('.nps__card-desc');

    gsap.to([chart, value, desc].filter(Boolean), {
      opacity: 0,
      y: 10,
      duration: 0.3,
      ease: 'power1.in',
      onComplete: () => {
        card.style.setProperty('--stat-value', '0');
        if (value) value.textContent = '0%';
        if (chart) gsap.set(chart, { scale: 0.85 });
      },
    });
  }

  _setSummaryFinal() {
    const col = this.closest('.nps__right');
    if (!col) return;
    col.querySelectorAll('.nps__summary-card').forEach((card) => {
      const target = parseFloat(card.dataset.summaryValue) || 0;
      const numEl = card.querySelector('.nps__summary-num');
      if (numEl) numEl.textContent = Math.round(target);
    });
  }

  _resetCardContent(card) {
    card.style.setProperty('--stat-value', '0');
    const value = card.querySelector('.nps__card-value');
    const desc = card.querySelector('.nps__card-desc');
    const chart = card.querySelector('.nps__card-chart');
    if (value) { gsap.set(value, { opacity: 0, y: 10 }); value.textContent = '0%'; }
    if (desc) gsap.set(desc, { opacity: 0, y: 10 });
    if (chart) gsap.set(chart, { opacity: 0, scale: 0.85 });
  }

  /* ───────────────────────────────────────────
     Carousel: auto-play with timer ring
     ─────────────────────────────────────────── */
  _startAutoplay() {
    if (this._autoplayTimer || !this._isCarousel) return;
    this._advanceAutoplay();
  }

  _advanceAutoplay() {
    this._animateTimerRing();
    this._autoplayTimer = setTimeout(() => {
      const dots = this._dots ? Array.from(this._dots.querySelectorAll('.nps__nav-dot')) : [];
      const currentIndex = dots.findIndex((d) => d.classList.contains('is-active'));
      const nextIndex = (currentIndex + 1) % this.cards.length;
      this.scrollTo({ left: this.cards[nextIndex].offsetLeft - 24, behavior: 'smooth' });
      // Next cycle is kicked off by the IntersectionObserver when the new card snaps in
    }, this._autoplayInterval);
  }

  _stopAutoplay() {
    clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
    this._resetTimerRing();
  }

  _pauseAutoplay() {
    this._autoplayPaused = true;
    this._stopAutoplay();
  }

  _resumeAutoplay() {
    this._autoplayPaused = false;
    setTimeout(() => {
      if (!this._autoplayPaused && this._isCarousel) this._startAutoplay();
    }, 1000);
  }

  _animateTimerRing() {
    this._resetTimerRing();
    if (!this._dots) return;
    const activeDot = this._dots.querySelector('.nps__nav-dot.is-active');
    if (!activeDot) return;

    this._timerStart = performance.now();
    this._timerRaf = requestAnimationFrame(() => this._tickTimer(activeDot));
  }

  _tickTimer(dot) {
    const elapsed = performance.now() - this._timerStart;
    const progress = Math.min(100, (elapsed / this._autoplayInterval) * 100);
    dot.style.setProperty('--dot-progress', progress);
    if (progress < 100) {
      this._timerRaf = requestAnimationFrame(() => this._tickTimer(dot));
    }
  }

  _resetTimerRing() {
    if (this._timerRaf) {
      cancelAnimationFrame(this._timerRaf);
      this._timerRaf = null;
    }
    if (!this._dots) return;
    this._dots.querySelectorAll('.nps__nav-dot').forEach((d) => {
      d.style.setProperty('--dot-progress', 0);
    });
  }

  /* ───────────────────────────────────────────
     Cleanup
     ─────────────────────────────────────────── */
  _destroy() {
    if (this.tl) {
      this.tl.scrollTrigger && this.tl.scrollTrigger.kill();
      this.tl.kill();
      this.tl = null;
    }

    if (this._pinTrigger) {
      this._pinTrigger.kill();
      this._pinTrigger = null;
    }

    if (this._observer) {
      this._observer.kill();
      this._observer = null;
    }

    if (this._dots) {
      this._dots.remove();
      this._dots = null;
    }

    if (this._onCardMouseMove) {
      this.cards.forEach((card) => {
        card.removeEventListener('mousemove', this._onCardMouseMove);
      });
      this._onCardMouseMove = null;
    }

    this._progressDriven = false;

    if (this._currentTL) {
      this._currentTL.kill();
      this._currentTL = null;
    }

    if (this._cardObserver) {
      this._cardObserver.disconnect();
      this._cardObserver = null;
    }

    // Autoplay cleanup
    clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
    this._autoplayPaused = false;
    if (this._timerRaf) { cancelAnimationFrame(this._timerRaf); this._timerRaf = null; }
    if (this._sectionObserver) {
      this._sectionObserver.disconnect();
      this._sectionObserver = null;
    }
    if (this._onTouchStart) {
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchend', this._onTouchEnd);
      this._onTouchStart = null;
      this._onTouchEnd = null;
    }

    this._isCarousel = false;
    this._isScrub = false;

    const col = this.closest('.nps__cards-col');
    if (col) col.classList.remove('nps__cards-col--stacking', 'nps__cards-col--carousel', 'nps__cards-col--scrub');
    this.classList.remove('is-stacking', 'is-carousel', 'is-scrub');

    (this.cards || []).forEach((c) => {
      if (c._ringTween) { c._ringTween.kill(); c._ringTween = null; }
      c._revealed = false;
    });

    this.entranceTriggers.forEach((st) => st && st.kill());
    this.entranceTriggers = [];
  }

  _debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

customElements.define('nps-cards', NpsCards);
